<template>
  <SubscribeForm
    v-model:email-address="emailAddress"
    v-model:metadata="metadata"
    v-model:turnstile-response-token="turnstileResponseToken"
    v-model:survey-responses="surveyResponses"
    :newsletter="newsletter"
    :subscriber="subscriber"
    :state="state"
    :variant="variant"
    :error="error"
    :surveys="surveys"
    :is-on-custom-domain="customDomain"
    :subscribe="subscribe"
    :resubscribe="resubscribe"
    :check-subscriber="checkSubscriber"
  />
</template>

<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { writable } from "@/lib/dirty";
import type { Newsletter } from "@/types/newsletter";

import { useResubscribe } from "./composables/useResubscribe";
import { useSubscribe } from "./composables/useSubscribe";
import { useSurveySubmission } from "./composables/useSurveySubmission";
import {
  type BasicSubscriber,
  type FormSurvey,
  type SubscribeFormVariant,
  type SubscriberFacingAuthentication,
  type SubscriptionState,
  calculateSubscriptionState,
  isOnCustomDomain,
  isValidEmail,
  parseError,
} from "./lib";
import SubscribeForm from "./SubscribeForm.vue";

interface Props {
  newsletter: Newsletter;
  subscriberFacingAuthentication?: SubscriberFacingAuthentication;
  variant: SubscribeFormVariant;
  onSubscribe?: (subscriber: BasicSubscriber) => void;
  surveys?: FormSurvey[];
  formId?: string | null;
}

const props = defineProps<Props>();

const emailAddress = ref(
  props.subscriberFacingAuthentication?.email_address || ""
);
const metadata = ref<Record<string, string>>({});
const surveyResponses = ref<Record<string, string | number | number[]>>({});
const turnstileResponseToken = ref("");
const subscriber = ref<BasicSubscriber | undefined>(
  props.subscriberFacingAuthentication?.subscribers?.find(
    (s) => s.newsletter_id === props.newsletter.id
  ) || undefined
);

const customDomain = isOnCustomDomain(props.newsletter);

// State follows subscriber/auth by default, but can be imperatively
// overwritten for transient states (errors, validation). Resets
// automatically when the upstream (subscriber/auth) changes.
// isPending is layered on top so that the pending→settled transition
// doesn't reset error overrides set by onError.
const baseState = writable(() => {
  return calculateSubscriptionState(
    subscriber.value,
    props.subscriberFacingAuthentication
  );
});

const state = computed<SubscriptionState>(() => {
  if (
    subscribeMutation.isPending.value ||
    resubscribeMutation.isPending.value
  ) {
    return "pending_subscription";
  }
  return baseState.value;
});

const error = ref("");

watch(subscriber, (sub) => {
  error.value = "";
  if (sub) {
    emailAddress.value = sub.email_address;
  }
  if (sub && (sub.type === "premium" || sub.type === "gifted")) {
    window.location.reload();
  }
});

const subscribeMutation = useSubscribe({
  newsletter: props.newsletter,
  onSuccess(sub) {
    subscriber.value = sub;
    metadata.value = {};

    if (props.formId && Object.keys(surveyResponses.value).length > 0) {
      surveySubmission.mutate({
        subscriberId: sub.id,
        responses: surveyResponses.value,
      });
    }

    props.onSubscribe?.(sub);

    if (props.newsletter.subscription_redirect_url) {
      window.location.href = props.newsletter.subscription_redirect_url;
    }
  },
  onError(err, data) {
    if (data?.subscriber) {
      subscriber.value = {
        id: data.subscriber.id as string,
        email_address: data.subscriber.email_address as string,
        type: data.subscriber.type as SubscriptionState,
        newsletter_id: props.newsletter.id,
      };
    } else {
      const parsed = parseError(err, data);
      if (customDomain && parsed.state === "invalid_ip_address") {
        baseState.value = "errored_email";
        error.value =
          "We're unable to process your subscription at this time. Please try again later.";
      } else {
        baseState.value = parsed.state;
        error.value = parsed.error;
      }
    }
  },
});

const resubscribeMutation = useResubscribe({
  newsletter: props.newsletter,
  onSuccess(sub) {
    subscriber.value = sub;
  },
  onError(errorMessage) {
    baseState.value = "errored_email";
    error.value = errorMessage;
  },
});

const surveySubmission = useSurveySubmission();

function subscribe() {
  subscribeMutation.mutate({
    emailAddress: emailAddress.value,
    metadata: metadata.value,
    turnstileResponseToken: turnstileResponseToken.value,
    formId: props.formId || null,
  });
}

function resubscribe(subscriberId: string) {
  resubscribeMutation.mutate(subscriberId);
}

function checkSubscriber() {
  if (!emailAddress.value) return;
  if (!isValidEmail(emailAddress.value)) {
    baseState.value = "invalid_email__regex";
    error.value = "That email address is invalid";
    return;
  }
  if (subscriber.value) return;
  baseState.value = "not_subscribed";
}

// Auto-submit when Turnstile token is received after invalid_ip_address
watch(turnstileResponseToken, (newToken) => {
  if (newToken && state.value === "invalid_ip_address") {
    subscribe();
  }
});
</script>
